import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  limit,
  writeBatch,
  arrayUnion,
} from "firebase/firestore";
import { MessageSquare, X } from "lucide-react";
import { db } from "../../firebase";

/**
 * Lightweight conversation drawer for dashboard pages.
 * - Shows active problems with unread counts and last message preview
 * - Mobile friendly: full-screen overlay on small screens, panel on desktop
 */
export default function ConversationDrawer({ problems = [], currentUserId }) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState({});
  const navigate = useNavigate();
  const prevUnreadRef = useRef({});

  // Aggregate unread across threads
  const totalUnread = useMemo(
    () =>
      Object.values(threads).reduce((sum, t) => {
        const count = Number(t.unread || 0);
        return sum + (Number.isNaN(count) ? 0 : count);
      }, 0),
    [threads]
  );

  useEffect(() => {
    if (!currentUserId || problems.length === 0) {
      setThreads({});
      return;
    }

    // Listen to messages for each active problem (full collection to keep unread count accurate)
    const unsubscribes = problems.map((p) => {
      const messagesRef = collection(db, "problems", p.id, "messages");
      const q = query(messagesRef, orderBy("createdAt", "desc"));

      return onSnapshot(
        q,
        (snap) => {
          let unread = 0;
          let last = null;

          snap.forEach((doc) => {
            const msg = doc.data();
            if (!last) {
              last = msg;
            }
            const alreadyRead =
              Array.isArray(msg.readBy) && msg.readBy.includes(currentUserId);
            const fromOther = msg.senderId && msg.senderId !== currentUserId;
            if (fromOther && !alreadyRead) {
              unread += 1;
            }
          });

          const counterpart =
            currentUserId === p.helperId
              ? p.clientName || p.clientEmail || "Requester"
              : p.helperName || p.helperEmail || "Specialist";

          // Notify on new unread messages
          const prevUnread = prevUnreadRef.current[p.id] || 0;
          const prevLastId = prevUnreadRef.current[`${p.id}-last`] || null;
          if (unread > prevUnread && last && last.senderId !== currentUserId) {
            sendNotification({
              title: `New message from ${last.senderName || counterpart}`,
              body: last.text || last.content || last.message || "",
              url: `${window.location.origin}/workspace/${p.id}`,
            });
          }
          prevUnreadRef.current[p.id] = unread;
          prevUnreadRef.current[`${p.id}-last`] = last?.id || prevLastId;

          setThreads((prev) => ({
            ...prev,
            [p.id]: {
              problemId: p.id,
              title: p.title || p.problemTitle || "Workspace",
              counterpart,
              unread,
              lastMessage: last?.text || last?.content || last?.message || "",
              lastSenderId: last?.senderId,
              lastSenderName: last?.senderName,
            },
          }));
        },
        (err) => {
          console.error("Conversation drawer listener error:", err);
        }
      );
    });

    return () => unsubscribes.forEach((unsub) => unsub && unsub());
  }, [problems, currentUserId]);

  const markThreadRead = async (problemId) => {
    if (!currentUserId) return;
    try {
      const messagesRef = collection(db, "problems", problemId, "messages");
      // Limit to recent messages to avoid heavy writes; adjust if needed
      const snap = await getDocs(query(messagesRef, orderBy("createdAt", "desc"), limit(100)));
      const batch = writeBatch(db);
      let hasUpdates = false;

      snap.forEach((docSnap) => {
        const msg = docSnap.data();
        const fromOther = msg.senderId && msg.senderId !== currentUserId;
        const alreadyRead = Array.isArray(msg.readBy) && msg.readBy.includes(currentUserId);
        if (fromOther && !alreadyRead) {
          batch.update(docSnap.ref, { readBy: arrayUnion(currentUserId) });
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        await batch.commit();
      }
    } catch (err) {
      console.warn("Failed to mark thread read:", err);
    }
  };

  const handleOpenWorkspace = async (problemId) => {
    await markThreadRead(problemId);
    setOpen(false);
    navigate(`/workspace/${problemId}`);
  };

  const sendNotification = ({ title, body, url }) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "granted") {
      const n = new Notification(title, { body, data: { url } });
      n.onclick = () => {
        window.open(url, "_blank");
      };
      return;
    }
    if (Notification.permission !== "denied") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") {
          const n = new Notification(title, { body, data: { url } });
          n.onclick = () => window.open(url, "_blank");
        }
      });
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Secondary by design: reading messages is not the primary action on
            a dashboard, so this must not outrank "Report an incident". */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative flex items-center gap-2 rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <MessageSquare className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Messages
          {totalUnread > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-rose-500 px-1.5 text-xs font-bold tabular-nums text-white">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-slate-900/30"
            onClick={() => setOpen(false)}
          />

          {/* Drawer */}
          <div className="relative ml-auto flex h-full w-full max-w-full flex-col bg-white shadow-xl transition-transform sm:w-96">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex items-center gap-2 font-semibold text-slate-900">
                <MessageSquare className="h-4 w-4 text-slate-400" aria-hidden="true" />
                <span>Messages</span>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {problems.length === 0 ? (
                <div className="p-4 text-sm text-slate-500">
                  No active workspaces yet.
                </div>
              ) : (
                problems.map((p) => {
                  const t = threads[p.id] || {};
                  const unread = t.unread || 0;
                  return (
                    <button
                      key={p.id}
                      onClick={() => handleOpenWorkspace(p.id)}
                      className="w-full border-b px-4 py-3 text-left hover:bg-slate-50"
                    >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 truncate">
                      {t.counterpart || "Workspace"}
                    </div>
                    {unread > 0 && (
                      <span className="inline-flex items-center justify-center rounded-full bg-rose-500 px-2 text-xs font-bold text-white">
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {p.title || p.problemTitle || "Workspace"}
                  </div>
                      {t.lastMessage ? (
                        <div className="mt-1 truncate text-sm text-slate-600">
                          {t.lastMessage}
                        </div>
                      ) : (
                        <div className="mt-1 text-sm text-slate-400">
                          No messages yet
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

